import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@hooks';
import { canAccessOwnerArea } from '@utils';
import { UserMenu } from './UserMenu';

interface LayoutProps {
  children: ReactNode;
}

// Every nav entry routes to a real page — no orphan routes, no dead links
// (fe_reachability). `ownerOnly` links are hidden for staff (F1.7); the
// OwnerRoute guard is the authoritative block for typed URLs / deep links.
const NAV_LINKS: Array<{
  to: string;
  label: string;
  icon: typeof HomeIcon;
  testid: string;
  ownerOnly?: boolean;
}> = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: HomeIcon,
    testid: 'nav-dashboard',
  },
  {
    to: '/documents',
    label: 'Documents',
    icon: DocumentTextIcon,
    testid: 'nav-documents',
  },
  {
    to: '/revenue',
    label: 'Revenue',
    icon: ChartBarIcon,
    testid: 'nav-revenue',
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Cog6ToothIcon,
    testid: 'nav-settings',
    ownerOnly: true,
  },
  {
    to: '/users',
    label: 'Users',
    icon: UsersIcon,
    testid: 'nav-users',
    ownerOnly: true,
  },
];

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[9px] border-[1.5px] border-white/45">
        <span className="font-serif text-[19px] italic text-white">L</span>
      </div>
      {!compact && (
        <div className="min-w-0">
          <div className="truncate font-serif text-[15px] leading-tight text-white">
            Lionel J Photography
          </div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#7fa8e0]">
            Invoicing
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarUser() {
  const { user } = useAuth();
  const parts = (user?.name?.trim().split(/\s+/) ?? []).filter(Boolean);
  const initials =
    parts.length >= 2
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
      : (parts[0]?.[0] ?? user?.email?.[0] ?? 'U').toUpperCase();
  const roleLabel = user?.role === 'owner' ? 'Owner' : 'Staff';

  return (
    <div className="flex items-center gap-3 border-t border-white/10 px-[18px] py-4">
      <div className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-[13px] font-semibold text-white">
        {initials}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-white">
          {user?.name || user?.email?.split('@')[0] || 'User'}
        </div>
        <div className="text-[11px] text-[#7fa8e0]">{roleLabel}</div>
      </div>
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user } = useAuth();
  const isOwner = canAccessOwnerArea(user);
  const navLinks = NAV_LINKS.filter(link => !link.ownerOnly || isOwner);

  return (
    <div className="min-h-screen bg-canvas">
      {/* Brand rail */}
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-navy-800 sm:flex"
        data-testid="app-sidebar"
      >
        <div className="flex h-[76px] items-center border-b border-white/10 px-[22px]">
          <Link to="/dashboard" aria-label="Lionel J Photography — Dashboard">
            <BrandMark />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 px-3.5 py-4">
          {navLinks.map(link => {
            const isActive =
              location.pathname === link.to ||
              location.pathname.startsWith(`${link.to}/`);
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testid}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-sm ${
                  isActive
                    ? 'bg-white/[0.12] font-semibold text-white'
                    : 'font-medium text-[#a9c3e8] hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon className="h-[19px] w-[19px] flex-shrink-0" />
                {link.label}
              </Link>
            );
          })}
        </nav>
        <SidebarUser />
      </aside>

      {/* Main column */}
      <div className="sm:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-hairline bg-white px-4 sm:px-6">
          {/* Mobile brand + inline nav (rail hidden < sm) */}
          <div className="flex items-center gap-3 sm:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy-800">
              <span className="font-serif text-sm italic text-white">L</span>
            </div>
            <nav className="flex items-center gap-1">
              {navLinks.map(link => {
                const isActive = location.pathname.startsWith(link.to);
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    aria-label={link.label}
                    className={`rounded-md p-2 ${
                      isActive ? 'text-primary-700' : 'text-gray-500'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="hidden font-mono text-[11px] uppercase tracking-[0.18em] text-gray-400 sm:block">
            {user?.role === 'owner' ? 'Owner workspace' : 'Studio workspace'}
          </div>
          <UserMenu />
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
